import React, { useRef, useState } from "react";
import { AlertTriangle, Camera, CheckCircle2, ChevronLeft, Loader2, X } from "lucide-react";
import { buildLeaveFormData, mockSubmitLeaveRequest } from "../../../../controller/mobile/Izinmockapi";
import { LEAVE_TYPE_LABEL, type LeaveType } from "../../../../controller/mobile/Izintypes";
import { todayDateOnly } from "../../../../controller/mobile/Dateutils";

interface IzinFormPageProps {
  idKaryawan: number;
  onCancel: () => void;
  onSuccess: () => void;
}

type SubmitState = "idle" | "loading" | "success" | "error";

const LEAVE_TYPE_OPTIONS: LeaveType[] = ["cuti", "izin", "sakit", "lainnya"];

export default function IzinFormPage({
  idKaryawan,
  onCancel,
  onSuccess,
}: IzinFormPageProps): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const today = todayDateOnly();
  const [type, setType] = useState<LeaveType>("cuti");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [keterangan, setKeterangan] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);

  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dateRangeInvalid = endDate < startDate;
  const canSubmit =
    startDate.length > 0 &&
    endDate.length > 0 &&
    !dateRangeInvalid &&
    submitState !== "loading";

  const handleAttachmentClick = () => fileInputRef.current?.click();

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setAttachment(file ?? null);
    e.target.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitState("loading");
    setErrorMessage(null);

    const formData = buildLeaveFormData({ idKaryawan, type, startDate, endDate, keterangan, attachment });

    const result = await mockSubmitLeaveRequest(formData);

        if (result.status === 201) {
      setSubmitState("success");
      onSuccess();
    } else {
      setSubmitState("error");
            setErrorMessage(String(result?.message ?? "Gagal mengajukan izin."));
    }
  };

  return (
    <div className="flex h-full flex-col">
    {/* Header */}
    <header className="flex shrink-0 items-center gap-3 rounded-b-3xl bg-blue-950 px-4 py-5 text-white">
        <button
        type="button"
        onClick={onCancel}
        className="rounded-full p-1.5 hover:bg-white/10"
        aria-label="Kembali"
        >
        <ChevronLeft className="h-5 w-5" />
        </button>
        <div>
        <p className="text-xs font-medium uppercase tracking-widest text-blue-200">
            Berhalangan Hadir
        </p>
        <h1 className="text-lg font-semibold">Ajukan Izin</h1>
        </div>
    </header>

    <main className="flex-1 overflow-y-auto px-5 py-5">
        {errorMessage && (
        <div className="mb-4 flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{errorMessage}</p>
        </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
        <div>
            <label
                htmlFor="type"
                className="mb-1.5 block text-xs font-medium text-slate-600"
            >Penyampaian Berupa</label>
            <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as LeaveType)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 focus:border-blue-950 focus:outline-none"
            >
            {LEAVE_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                {LEAVE_TYPE_LABEL[option]}
                </option>
            ))}
            </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
            <div>
            <label
                htmlFor="start_date"
                className="mb-1.5 block text-xs font-medium text-slate-600"
            >
                Mulai
            </label>
            <input
                id="start_date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800 focus:border-blue-950 focus:outline-none"
            />
            </div>
            <div>
            <label
                htmlFor="end_date"
                className="mb-1.5 block text-xs font-medium text-slate-600"
            >
                Selesai
            </label>
            <input
                id="end_date"
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-800 focus:border-blue-950 focus:outline-none"
            />
            </div>
        </div>
        {dateRangeInvalid && (
            <p className="-mt-2 text-xs text-rose-600">
            Tanggal selesai tidak boleh sebelum tanggal mulai.
            </p>
        )}

        <div>
            <p className="mb-1.5 text-xs font-medium text-slate-600">
            Foto Lampiran (kalo ada)
            </p>
            <button
            type="button"
            onClick={handleAttachmentClick}
            className="flex w-full items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-500 hover:border-slate-300"
            >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100">
                <Camera className="h-4 w-4 text-slate-500" />
            </span>
            <span className="flex-1 truncate">
                {attachment ? attachment.name : "Pilih foto atau dokumen"}
            </span>
            {attachment && (
                <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                    e.stopPropagation();
                    setAttachment(null);
                }}
                className="shrink-0 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                <X className="h-3.5 w-3.5" />
                </span>
            )}
            </button>
            <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleAttachmentChange}
            className="hidden"
            />
        </div>

        <div>
            <label
            htmlFor="keterangan"
            className="mb-1.5 block text-xs font-medium text-slate-600"
            >
            Keterangan
            </label>
            <textarea
            id="keterangan"
            rows={3}
            value={keterangan}
            onChange={(e) => setKeterangan(e.target.value)}
            placeholder="Tulis alasan atau keterangan tambahan..."
            className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-950 focus:outline-none"
            />
        </div>
        </form>
    </main>

    {/* Submit bar */}
    <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4">
        <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-950 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-blue-900 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
        >
        {submitState === "loading" ? (
            <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Mengirim...
            </>
        ) : submitState === "success" ? (
            <>
            <CheckCircle2 className="h-4 w-4" />
            Terkirim
            </>
        ) : (
            "Kirim"
        )}
        </button>
    </footer>
    </div>
  );
}